/* jshint esversion: 6 */

/**
 * ResponseAPIService is a service class that handles operations related to object retrieval,
 * attachment management, and status updates for a given UUID.
 */
class ResponseAPIService {
    /**
     * Constructs a new instance of ResponseAPIService.
     * @param {Object} $config - The configuration object.
     * @param {Object} $bridges - The bridges object.
     * @param {Object} ObjectInstanceRegistrationService - The service for object instance registration.
     */
    constructor($config, $bridges, ObjectInstanceRegistrationService) {
        this.$config = $config;
        this.$bridges = $bridges;
        this.ObjectInstanceRegistrationService = ObjectInstanceRegistrationService;
    }

    /**
     * Retrieves an object by its UUID.
     * @param {string} uuid - The UUID of the object.
     * @returns {Object} The object corresponding to the UUID.
     */
    get_object(uuid) {
        return this.ObjectInstanceRegistrationService.getObjectByUUID(uuid);
    }

    /**
     * Retrieves the attachment of an object by its UUID.
     * @param {string} uuid - The UUID of the object.
     * @returns {Object} The attachment of the object.
     */
    get_attachment(uuid) {
        return this.get_object(uuid).contentObject.getAttachement() || {};
    }

    /**
     * Sets a key-value pair in the attachment of an object.
     * @param {string} key - The key to set.
     * @param {*} value - The value to set.
     * @param {string} uuid - The UUID of the object.
     * @throws {Error} If the key is not a string.
     */
    set(key, value, uuid) {
        if (typeof key !== 'string') {
            throw new Error('Key need to be a string');
        }

        let content_object = this.get_object(uuid).contentObject;
        let attachment = this.get_attachment(uuid);
        if (typeof attachment !== 'object') {
            attachment = {};
        }

        attachment[key] = value;
        content_object.setAttachment(attachment);
        content_object.save();
    }

    /**
     * Retrieves the value of a key from the attachment of an object.
     * @param {string} key - The key to retrieve.
     * @param {string} uuid - The UUID of the object.
     * @returns {*} The value of the key, or false if the key is not found.
     */
    get(key, uuid) {
        return this.get_attachment(uuid)[key] || false;
    }

    /**
     * Checks if a key exists in the attachment of an object.
     * @param {string} key - The key to check.
     * @param {string} uuid - The UUID of the object.
     * @returns {boolean} True if the key exists, otherwise false.
     */
    has(key, uuid) {
        return this.get_attachment(uuid)[key] !== undefined;
    }

    /**
     * Retrieves multiple key-value pairs from the attachment of an object.
     * @param {Array} keys - The array of keys to retrieve.
     * @param {string} uuid - The UUID of the object.
     * @returns {Object} An object containing the key-value pairs.
     * @throws {Error} If the keys are not an array.
     */
    get_multiple(keys, uuid) {
        if (!Array.isArray(keys)) {
            throw new Error('Keys must be an array!');
        }

        let result = {};
        keys.forEach(key => {
            result[key] = this.get(key, uuid);
        });

        return result;
    }

    /**
     * Updates the status of an object by advancing its question part status.
     * @param {string} uuid - The UUID of the object.
     */
    update_status(uuid) {
        this.get_object(uuid).advanceQuestionPartStatus();
    }
}

export default ResponseAPIService;